import { SimWorld, addLog, isGroupModeEnabled, isGroupFixedPopulation } from '../types/SimWorld';
import { SimNPC, createSimNPC } from '../types/SimNPC';
import { createFamilyRelation } from '../types/Family';
import { getPartner } from './FamilyManager';
import { breedDecks } from '../genetics/DeckBreeder';
import { generateChildName } from '../utils/NameGenerator';
import type { ExtendedCardData } from '../../utils/cardManager';
import { POPULATION_CONSTANTS } from '../core/PopulationConstants';
import { MAP_CONFIG } from '../types/GameMap';
import type { GroupConfig } from '../types/Group';

// 定数を一元管理から取得
const {
  ADULT_AGE,
  BIRTH_BASE_RATE,
  BIRTH_MAX_CHILDREN,
  MIN_BATTLES_FOR_RATE,
} = POPULATION_CONSTANTS;

/**
 * 出生処理
 * @param maxPopulation 最大人口（密度ペナルティ計算用）
 */
export function processBirths(
  world: SimWorld,
  allCards: ExtendedCardData[],
  maxPopulation: number = 200
): void {
  const { npcs, day } = world;
  const newborns: SimNPC[] = [];
  const currentPopulation = npcs.filter((n) => n.isAlive).length;

  for (const npc of npcs) {
    if (!npc.isAlive || !npc.hasPartner) continue;

    // 固定人口モードのグループは通常出生をスキップ（リスポーンで管理）
    if (npc.group && isGroupFixedPopulation(npc.group, world)) continue;

    // 成人チェック
    if (npc.age < ADULT_AGE) continue;

    const partner = getPartner(npc, npcs);
    if (!partner || !partner.isAlive) continue;

    // パートナーも成人であること
    if (partner.age < ADULT_AGE) continue;

    // 子供の数制限チェック
    if (
      npc.childrenCount >= BIRTH_MAX_CHILDREN ||
      partner.childrenCount >= BIRTH_MAX_CHILDREN
    ) {
      continue; // どちらかが上限に達していたら出産しない
    }

    // 出生判定（1回だけ判定、重複を避ける）
    if (npc.id < partner.id) {
      // IDでソートして片方だけ処理
      const birthChance = calculateBirthChance(
        npc,
        partner,
        currentPopulation,
        maxPopulation
      );

      if (Math.random() < birthChance) {
        const child = createChild(npc, partner, day, allCards, world);
        newborns.push(child);

        // 両親の子供カウントを増やす
        npc.childrenCount++;
        partner.childrenCount++;

        addLog(
          world,
          `👶 ${npc.name}と${partner.name}の間に${child.name}が生まれた！`
        );
      }
    }
  }

  // 新生児を追加
  npcs.push(...newborns);
}

/**
 * 勝率計算
 */
function getWinRate(npc: SimNPC): number {
  const totalBattles = npc.wins + npc.losses;
  // 戦闘経験が少ない場合は0.5とみなす
  if (totalBattles < MIN_BATTLES_FOR_RATE) {
    return 0.5;
  }
  return npc.wins / totalBattles;
}

/**
 * 出生確率計算（密度ペナルティ + 強者生存モデル + 子供なし/寿命ボーナス）
 */
function calculateBirthChance(
  parent1: SimNPC,
  parent2: SimNPC,
  currentPopulation: number,
  maxPopulation: number
): number {
  // 密度ペナルティ（人口が最大に近づくほど出生率低下）
  // (1 - (pop/max)^2) で急激に低下
  const densityRatio = currentPopulation / maxPopulation;
  const densityPenalty = Math.max(0, 1 - densityRatio * densityRatio);

  // 強者ボーナス（勝率が高いほど出生しやすい）
  const avgWinRate = (getWinRate(parent1) + getWinRate(parent2)) / 2;
  // 勝率50%で1.0倍、勝率100%で1.5倍、勝率0%で0.5倍
  const strengthBonus = 0.5 + avgWinRate;

  // 子供なしボーナス（どちらも子供がいない場合）
  const hasNoChildren = parent1.childrenCount === 0 && parent2.childrenCount === 0;
  const noChildrenBonus = hasNoChildren ? 1.5 : 1.0;

  // 残り寿命ボーナス（どちらかの寿命が少ない場合）
  const remaining1 = parent1.maxLifespan - parent1.age;
  const remaining2 = parent2.maxLifespan - parent2.age;
  const minRemaining = Math.min(remaining1, remaining2);
  let lifespanBonus = 1.0;
  if (minRemaining <= 10) {
    lifespanBonus = 2.0;
  } else if (minRemaining <= 20) {
    lifespanBonus = 1.5;
  }

  // 子供なし × 寿命少ない の場合は追加ボーナス（焦りMAX）
  let urgencyBonus = 1.0;
  if (hasNoChildren && minRemaining <= 10) {
    urgencyBonus = 2.0; // 合計で 1.5 × 2.0 × 2.0 = 6倍
  } else if (hasNoChildren && minRemaining <= 20) {
    urgencyBonus = 1.5; // 合計で 1.5 × 1.5 × 1.5 = 3.4倍
  }

  // 最終出生率
  const finalRate = BIRTH_BASE_RATE * densityPenalty * strengthBonus * noChildrenBonus * lifespanBonus * urgencyBonus;

  return finalRate;
}

/**
 * 子供を作成
 * グループモードでは親のグループを継承し、グループのカードプールからデッキを生成
 */
function createChild(
  parent1: SimNPC,
  parent2: SimNPC,
  birthDay: number,
  allCards: ExtendedCardData[],
  world: SimWorld
): SimNPC {
  // 名前生成（親の苗字を引き継ぐ・重複回避）
  const existingNames = new Set(world.npcs.map((n) => n.name));
  let childName = generateChildName(parent1.name, parent2.name);

  // 重複チェック（稀に同じ名前になる可能性があるため）
  let attempts = 0;
  while (existingNames.has(childName) && attempts < 100) {
    childName = generateChildName(parent1.name, parent2.name);
    attempts++;
  }

  // それでも重複する場合は番号を付ける
  if (existingNames.has(childName)) {
    let counter = 1;
    while (existingNames.has(`${childName} ${counter}`)) {
      counter++;
    }
    childName = `${childName} ${counter}`;
  }

  // グループモードの場合、親のグループを継承
  const groupModeEnabled = isGroupModeEnabled(world);
  const childGroup = groupModeEnabled ? parent1.group : undefined;

  // グループのカードプール設定を取得
  let cardPoolConfig = world.cardPoolConfig;
  if (groupModeEnabled && childGroup && world.groupModeConfig) {
    const groupConfig = world.groupModeConfig.groups.find(g => g.id === childGroup);
    if (groupConfig) {
      cardPoolConfig = groupConfig.cardPoolConfig;
    }
  }

  // デッキ遺伝（グループのカードプール設定を適用）
  const childDeck = breedDecks(parent1, parent2, allCards, cardPoolConfig);

  // 世代計算: 両親の世代の最大値 + 1
  const childGeneration = Math.max(parent1.generation, parent2.generation) + 1;

  // 出生位置: 親の近く（±1マス）に生まれる（マップ境界内に制限）
  const parentPos = parent1.position || parent2.position || { x: 0, y: 0 };
  const childPosition = {
    x: Math.max(0, Math.min(MAP_CONFIG.WIDTH - 1, parentPos.x + Math.floor(Math.random() * 3) - 1)),
    y: Math.max(0, Math.min(MAP_CONFIG.HEIGHT - 1, parentPos.y + Math.floor(Math.random() * 3) - 1)),
  };

  // NPC作成（グループを継承）
  const child = createSimNPC(childName, childDeck, {
    generation: childGeneration,
    birthDay,
    position: childPosition,
    group: childGroup,
  });

  // 家族関係設定
  child.family = [
    createFamilyRelation(parent1.id, 'parent', birthDay),
    createFamilyRelation(parent2.id, 'parent', birthDay),
  ];

  parent1.family.push(createFamilyRelation(child.id, 'child', birthDay));
  parent2.family.push(createFamilyRelation(child.id, 'child', birthDay));

  // グループ統計の出生数を更新
  if (groupModeEnabled && childGroup && world.groupStats) {
    const stats = world.groupStats.get(childGroup);
    if (stats) {
      stats.birthCount++;
      stats.population++;
    }
  }

  // レーティングは初期値のまま（createSimNPCのデフォルト1000）
  // Eloレーティングなので親からの遺伝は不要（実力で決まる）

  return child;
}
